import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import CreateExpenseCategoryModal from 'src/component-modal/CreateExpenseCategoryModal/CreateExpenseCategoryModal'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { organizationIntegrationsUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import { selectBrandFeaturesFromState } from 'src/redux/selectors/session.selectors'
import styled, { css } from 'styled-components'

const Message = styled.div`
  ${getFontCss(
    16,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
`

const ExpenseCategoriesListEmptyState: React.FC = (): React.ReactElement => {
  const orgId = useSelector(selectOrganizationIdFromRoute)
  const listSearchImage = useColorOrGrayscaleImage(ImageNames.ListSearch)
  const { integrationsEnabled } = useSelector(selectBrandFeaturesFromState)
  const { enable_custom_fields_expense_fields: customFieldsExpenseFieldsEnabled } =
    useSelector(selectFeatureFlaggerFromState) ?? {}

  const { EDIT_EXPENSE_CATEGORIES: hasEditExpCatPermissions } =
    useQueryUserFeatures([UserFeature.EDIT_EXPENSE_CATEGORIES]).data || {}

  const [isCreateExpCatModalOpen, setIsCreateExpCatModalOpen] = useState(false)

  return (
    <>
      <CreateExpenseCategoryModal
        isOpen={isCreateExpCatModalOpen}
        onClose={() => setIsCreateExpCatModalOpen(false)}
      />
      <div
        css={css`
          width: 600px;
          display: flex;
          flex-direction: column;
          align-self: center;
          align-items: center;
          text-align: center;
          gap: 20px;
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            width: 560px;
          `)}
          ${getBreakpointDown(breakpointSizes.XXSM)(css`
            width: 100%;
            padding: 0 20px;
          `)}
        `}
        data-testid={'emptyExpCatList'}
      >
        <img src={listSearchImage} alt={''} />
        <div
          css={css`
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )}
            text-align: center;
          `}
        >
          {hasEditExpCatPermissions ? (
            <Trns source={'Set Up Expense Categories'} />
          ) : (
            <Trns
              source={"You don't have permission to set up expense categories"}
            />
          )}
        </div>
        <Message>
          {hasEditExpCatPermissions ? (
            <Trns
              source={
                'Make reconciliation seamless and organize your transactions by tagging them with expense categories.'
              }
            />
          ) : (
            <Trns
              source={
                "Looks like you don't have access to create expense categories. In order to get access, contact your org admin."
              }
            />
          )}
        </Message>
        <Message>
          {hasEditExpCatPermissions ? (
            integrationsEnabled ? (
              <Trns
                source={
                  'To get started, you can either create custom categories for your organization or connect to your accounting software.'
                }
              />
            ) : (
              <Trns
                source={
                  'To get started, create custom categories for your organization.'
                }
              />
            )
          ) : (
            <Trns
              source={
                "You'll still be able to see all expense categories here."
              }
            />
          )}
        </Message>
        <Message>
          <Trns
            source={
              'Learn more about Expense Categories in our ${resourceCenter}.'
            }
            expressions={{
              resourceCenter: (
                <ResourceCenterLink
                  size={HoverableLinkSizes.Large}
                  underline
                  regular
                />
              ),
            }}
          />
        </Message>
        {hasEditExpCatPermissions && (
          <div
            css={css`
              display: flex;
              flex-direction: row;
              margin-top: 20px;
              gap: 20px;
              ${getBreakpointDown(breakpointSizes.XXSM)(css`
                flex-direction: column;
              `)}
            `}
          >
            <Button
              testId={'new-category-empty'}
              type={ButtonTypes.button}
              design={ButtonDesigns.designCta}
              text={
                <Trns
                  source={
                    customFieldsExpenseFieldsEnabled
                      ? 'New Field +'
                      : 'New Category +'
                  }
                />
              }
              onClick={() => setIsCreateExpCatModalOpen(true)}
            />
            {integrationsEnabled && (
              <HoverableLink
                ariaLabelSource={'Connect to Accounting Software'}
                to={organizationIntegrationsUrl(orgId)}
                testId={'connectToERPBtn'}
              >
                <Trns source={'Connect to Accounting Software'} />
              </HoverableLink>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default ExpenseCategoriesListEmptyState
